import { Controller } from "@hotwired/stimulus"

// Manages the Resources panel on the book detail page — opens the "Add
// resource" modal, swaps the URL hint based on the chosen type, posts the
// resource via fetch (the modal cannot be a real <form> because it lives
// inside the book edit form), and handles delete with a confirm dialog.
export default class extends Controller {
  static targets = ["modal", "form", "urlInput", "urlHint", "typeSelect", "titleInput", "uploadedUrl", "submitButton"]
  static values = { createUrl: String }

  openModal() {
    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    setTimeout(() => this.urlInputTarget?.focus(), 50)
  }

  closeModal() {
    this.modalTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }

  // Enter-key inside any of the modal inputs triggers Add Resource — same UX
  // a real <form> would give for free.
  enterSubmit(event) {
    // Skip when the focus is on a textarea (Enter should insert a newline).
    if (event.target.tagName === "TEXTAREA") return
    event.preventDefault()
    this.submit(event)
  }

  // POST the resource fields to the create endpoint. Replaces what a real
  // <form> submit would do — we can't use a <form> here because the modal is
  // rendered inside the book edit form and nested forms aren't allowed.
  async submit(event) {
    event?.preventDefault()
    if (!this.hasFormTarget) return

    const url = this.urlInputTarget?.value?.trim()
    if (!url) {
      this.urlInputTarget?.focus()
      this.urlInputTarget?.classList.add("ring-2", "ring-red-400")
      setTimeout(() => this.urlInputTarget?.classList.remove("ring-2", "ring-red-400"), 1500)
      return
    }

    const endpoint = this.createUrlValue || this.element.dataset.bookResourcesCreateUrlValue
    if (!endpoint) {
      console.error("book-resources: createUrl value not set; cannot submit")
      return
    }

    const payload = new FormData()
    this.formTarget.querySelectorAll("input[name], select[name], textarea[name]").forEach((el) => {
      payload.append(el.name, el.value || "")
    })

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this._originalSubmitText = this.submitButtonTarget.textContent
      this.submitButtonTarget.textContent = "Saving…"
    }

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          Accept: "application/json"
        },
        body: payload
      })

      if (response.ok) {
        window.location.reload()
      } else {
        const text = await response.text()
        console.error("Add resource failed:", text)
        alert("Couldn't add that resource. Check the URL and try again.")
        this._restoreSubmitButton()
      }
    } catch (err) {
      console.error("Add resource error:", err)
      this._restoreSubmitButton()
    }
  }

  _restoreSubmitButton() {
    if (!this.hasSubmitButtonTarget) return
    this.submitButtonTarget.disabled = false
    if (this._originalSubmitText) this.submitButtonTarget.textContent = this._originalSubmitText
  }

  typeChanged(event) {
    const type = event.target.value
    if (!this.hasUrlHintTarget) return
    const hints = {
      video_link: "Paste a YouTube or Vimeo URL — we'll generate a thumbnail automatically.",
      pdf: "Paste a direct link to a PDF (e.g., from Google Drive, Dropbox, or your own host).",
      audio_link: "Paste a podcast episode or audio file URL.",
      external_link: "Paste any web page URL — an article, a review, etc."
    }
    this.urlHintTarget.textContent = hints[type] || ""
  }

  // When the user pastes a URL, try to auto-detect the type as a courtesy.
  urlChanged(event) {
    const url = event.target.value.trim()
    if (!url) return
    const select = this.formTarget.querySelector('[name="medium[media_type]"]')
    if (!select) return

    let detected = null
    if (/youtube\.com|youtu\.be|vimeo\.com/i.test(url)) detected = "video_link"
    else if (/\.pdf(\?|$)/i.test(url)) detected = "pdf"
    else if (/\.(mp3|m4a|wav|ogg)(\?|$)/i.test(url) || /soundcloud\.com|spotify\.com.*episode/i.test(url)) detected = "audio_link"

    if (detected && select.value !== detected) {
      select.value = detected
      select.dispatchEvent(new Event("change"))
    }
  }

  // Fired by the filestack controller after a successful drop/click upload.
  // Populates the URL field, picks a sensible type from MIME, and pre-fills
  // the title from the filename so the user just clicks "Add resource".
  fileUploaded(event) {
    const { url, filename, mimetype } = event.detail || {}
    if (!url) return

    if (this.hasUrlInputTarget) this.urlInputTarget.value = url

    if (this.hasTypeSelectTarget) {
      const type = this.detectTypeFromMime(mimetype)
      if (type) {
        this.typeSelectTarget.value = type
        this.typeSelectTarget.dispatchEvent(new Event("change"))
      }
    }

    if (this.hasTitleInputTarget && filename && !this.titleInputTarget.value) {
      // Strip extension and replace separators with spaces
      this.titleInputTarget.value = filename.replace(/\.[^/.]+$/, "").replace(/[-_]+/g, " ")
    }
  }

  detectTypeFromMime(mime = "") {
    if (mime.startsWith("image/")) return "image"
    if (mime === "application/pdf") return "pdf"
    if (mime.startsWith("audio/")) return "audio_link"
    if (mime.startsWith("video/")) return "video_link"
    if (mime.includes("word") || mime.includes("document") || mime === "application/msword") return "other"
    return null
  }

  delete(event) {
    if (!confirm("Remove this resource?")) return
    const url = event.currentTarget.dataset.url
    fetch(url, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        Accept: "application/json"
      }
    }).then((res) => {
      if (res.ok) window.location.reload()
    })
  }
}
